import React, { useState, useEffect } from 'react';
import { Menu, X, Github, Linkedin, ArrowRight, ArrowLeft } from 'lucide-react';
import './App.css';

// Pages
import HomePage from './pages/HomePage';
import StoryPage from './pages/StoryPage';
import ProjectsPage from './pages/ProjectsPage';
import ExperiencePage from './pages/ExperiencePage';
import SkillsPage from './pages/SkillsPage';
import ContactPage from './pages/ContactPage';
import PipelinePage from './pages/PipelinePage';
import FinePrintPage from './pages/FinePrintPage';

// --- NAV BAR ---
const NavBar = ({ currentPage, setCurrentPage, pages, isOpen, setIsOpen }) => (
  <nav className="navbar-custom">
    <div className="navbar-container">
      <a href="#home" className="navbar-logo" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }}>
        Adwoa B. Acheampong
      </a>
      <ul className={`nav-menu${isOpen ? ' open' : ''}`}>
        {pages.map(page => (
          <li key={page.id}>
            <button
              className={`nav-tab ${currentPage === page.id ? 'active' : ''}`}
              onClick={() => {
                setCurrentPage(page.id);
                setIsOpen(false);
              }}
            >
              {page.label}
            </button>
          </li>
        ))}
      </ul>
      <button className="menu-toggle" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
    </div>
  </nav>
);

// --- PAGE NAV CTA (prev + next) ---
const PageNavCTA = ({ prevPage, nextPage, onNavigate }) => {
  if (!prevPage && !nextPage) return null;
  return (
    <div className="next-page-cta">
      <div className="next-page-inner page-nav-row">
        {prevPage ? (
          <button className="next-page-btn page-nav-prev" onClick={() => onNavigate(prevPage.id)}>
            <ArrowLeft size={18} />
            <span className="next-page-title">{prevPage.label}</span>
          </button>
        ) : <span />}
        {nextPage ? (
          <button className="next-page-btn" onClick={() => onNavigate(nextPage.id)}>
            <span className="next-page-title">{nextPage.label}</span>
            <ArrowRight size={18} />
          </button>
        ) : <span />}
      </div>
    </div>
  );
};

// --- FOOTER ---
const Footer = ({ onFinePrint }) => (
  <footer className="site-footer">
    <div className="kente-border" style={{ marginTop: 0 }}></div>
    <div className="footer-socials">
      <a href="https://github.com/adwoa-acheampong" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
        <Github size={24} />
      </a>
      <a href="https://linkedin.com/in/adwoa-acheampong" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
        <Linkedin size={24} />
      </a>
    </div>
    <p>&copy; 2026 Adwoa B. Acheampong &ndash; Powered by Ghanaian Spirit & Innovation</p>
    <button className="footer-fineprint-link" onClick={onFinePrint}>
      Fine Print
    </button>
  </footer>
);

// --- MAIN APP ---
const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  const pages = [
    { id: 'home', label: 'Home' },
    { id: 'story', label: 'Story' },
    { id: 'projects', label: 'Projects' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'pipeline', label: 'Pipeline' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const navigateTo = (id) => setCurrentPage(id);
  const currentIndex = pages.findIndex(p => p.id === currentPage);
  const nextPage = currentIndex >= 0 && currentIndex < pages.length - 1 ? pages[currentIndex + 1] : null;
  const prevPage = currentIndex > 0 ? pages[currentIndex - 1] : null;

  const renderPage = () => {
    switch (currentPage) {
      case 'story': return <StoryPage />;
      case 'projects': return <ProjectsPage />;
      case 'experience': return <ExperiencePage />;
      case 'skills': return <SkillsPage />;
      case 'pipeline': return <PipelinePage />;
      case 'contact': return <ContactPage />;
      case 'fineprint': return <FinePrintPage />;
      default: return <HomePage />;
    }
  };

  return (
    <div>
      <NavBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pages={pages}
        isOpen={menuOpen}
        setIsOpen={setMenuOpen}
      />
      {renderPage()}
      {currentPage !== 'fineprint' && <PageNavCTA prevPage={prevPage} nextPage={nextPage} onNavigate={navigateTo} />}
      {currentPage === 'fineprint' && (
        <div className="next-page-cta">
          <div className="next-page-inner">
            <button className="next-page-btn page-nav-prev" onClick={() => navigateTo('home')}>
              <ArrowLeft size={18} />
              <span className="next-page-title">Back to Home</span>
            </button>
          </div>
        </div>
      )}
      <Footer onFinePrint={() => setCurrentPage('fineprint')} />
    </div>
  );
};

export default App;
